import {
  ApiError, createUser, listUsers, updateUser, type ManagedUser, type ManagedUserUpdate,
} from "@/api";
import { useAuth } from "@/auth/AuthContext";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ADMIN_ROLE } from "@/lib/adminNav";
import { type FormEvent, useEffect, useState } from "react";

const MIN_PASSWORD_LENGTH = 10;

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  operator: "Operator",
};

type Form = {
  principal: string;
  display_name: string;
  email: string;
  role: string;
  active: boolean;
  password: string;
};

const EMPTY_FORM: Form = {
  principal: "", display_name: "", email: "", role: "operator", active: true, password: "",
};

function errorText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  if (err.status === 409) return "Diesen Benutzernamen gibt es bereits.";
  if (err.status === 400) return "Das eigene Konto kann weder deaktiviert noch herabgestuft werden.";
  return err.message;
}

export function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    listUsers()
      .then((items) => { if (active) setUsers(items); })
      .catch((err: unknown) => {
        if (active) setError(errorText(err, "Benutzer konnten nicht geladen werden."));
      });
    return () => { active = false; };
  }, []);

  function replace(updated: ManagedUser) {
    setUsers((previous) => {
      const rest = (previous ?? []).filter((item) => item.id !== updated.id);
      return [...rest, updated].sort((a, b) => a.principal.localeCompare(b.principal));
    });
  }

  function startCreate() {
    setEditingId("new");
    setForm(EMPTY_FORM);
    setError(null);
  }

  function startEdit(user: ManagedUser) {
    setEditingId(user.id);
    setForm({
      principal: user.principal, display_name: user.display_name, email: user.email,
      role: user.role, active: user.active, password: "",
    });
    setError(null);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const password = form.password ? form.password : null;
    try {
      if (editingId === "new") {
        replace(await createUser({
          principal: form.principal.trim(), display_name: form.display_name.trim(),
          email: form.email.trim(), role: form.role, active: form.active, password,
        }));
      } else if (editingId) {
        const body: ManagedUserUpdate = {
          display_name: form.display_name.trim(), email: form.email.trim(),
          role: form.role, active: form.active,
        };
        if (password) body.password = password;
        replace(await updateUser(editingId, body));
      }
      setEditingId(null);
    } catch (err) {
      setError(errorText(err, "Benutzer konnte nicht gespeichert werden."));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(user: ManagedUser) {
    setBusy(true);
    setError(null);
    try {
      replace(await updateUser(user.id, { active: !user.active }));
    } catch (err) {
      setError(errorText(err, "Benutzer konnte nicht geändert werden."));
    } finally {
      setBusy(false);
    }
  }

  const editingSelf = editingId !== null && editingId === me?.id;
  const passwordTooShort = form.password.length > 0 && form.password.length < MIN_PASSWORD_LENGTH;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold">Benutzer</h1>
          <p className="mt-1 text-sm text-muted">
            Anmeldung per Kerberos mit dem Benutzernamen ohne Realm, optional zusätzlich per
            Passwort. Konten werden nie automatisch angelegt.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={startCreate} disabled={users === null}>
          Neuer Benutzer
        </Button>
      </div>
      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}

      {editingId ? (
        <form onSubmit={(event) => void onSubmit(event)} className="space-y-3 rounded-lg border border-hairline bg-surface p-4">
          <h2 className="text-sm font-semibold">
            {editingId === "new" ? "Neuer Benutzer" : `Benutzer „${form.principal}“ bearbeiten`}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {editingId === "new" ? (
              <div>
                <label className="field-label" htmlFor="user-principal">Benutzername</label>
                <input id="user-principal" className="field font-mono" required maxLength={255}
                  pattern="[A-Za-z0-9][A-Za-z0-9._\-]*" placeholder="z. B. jdoe" autoComplete="off"
                  spellCheck={false} value={form.principal}
                  onChange={(event) => setForm((prev) => ({ ...prev, principal: event.target.value }))} />
                <p className="mt-1 text-[11px] text-muted">Wie im Kerberos-Ticket, ohne @REALM.</p>
              </div>
            ) : null}
            <div>
              <label className="field-label" htmlFor="user-display-name">Anzeigename</label>
              <input id="user-display-name" className="field" required maxLength={255}
                value={form.display_name}
                onChange={(event) => setForm((prev) => ({ ...prev, display_name: event.target.value }))} />
            </div>
            <div>
              <label className="field-label" htmlFor="user-email">E-Mail</label>
              <input id="user-email" type="email" className="field" required maxLength={255}
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))} />
            </div>
            <div>
              <label className="field-label" htmlFor="user-role">Rolle</label>
              <select id="user-role" className="field" value={form.role} disabled={editingSelf}
                onChange={(event) => setForm((prev) => ({ ...prev, role: event.target.value }))}>
                <option value="operator">Operator: Mails bearbeiten und versenden</option>
                <option value={ADMIN_ROLE}>Admin: zusätzlich Administration</option>
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="user-password">
                {editingId === "new" ? "Passwort (optional)" : "Neues Passwort (optional)"}
              </label>
              <input id="user-password" type="password" className="field" autoComplete="new-password"
                maxLength={256} value={form.password}
                onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))} />
              <p className={passwordTooShort ? "mt-1 text-[11px] text-danger" : "mt-1 text-[11px] text-muted"}>
                {passwordTooShort
                  ? `Mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`
                  : editingId === "new"
                    ? "Leer lassen für reine Kerberos-Anmeldung."
                    : "Leer lassen, um das Passwort nicht zu ändern."}
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} disabled={editingSelf}
              onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))} />
            Aktiv (darf sich anmelden)
          </label>
          {editingSelf ? (
            <p className="text-[11px] text-muted">Rolle und Status des eigenen Kontos sind nicht änderbar.</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setEditingId(null)}>Abbrechen</Button>
            <Button type="submit" variant="primary" disabled={busy || passwordTooShort}>
              {busy ? "Speichern…" : "Speichern"}
            </Button>
          </div>
        </form>
      ) : null}

      {users === null && !error ? <div className="h-24 animate-pulse rounded-md bg-surface-subtle" /> : null}
      {users ? (
        <ul className="divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-surface">
          {users.map((user) => (
            <li key={user.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={user.active ? "truncate text-sm font-medium" : "truncate text-sm font-medium text-muted"}>
                    {user.display_name}
                  </span>
                  <Badge tone={user.role === ADMIN_ROLE ? "accent" : "default"}>
                    {ROLE_LABELS[user.role] ?? user.role}
                  </Badge>
                  {!user.active ? <Badge tone="muted">Inaktiv</Badge> : null}
                  {user.id === me?.id ? <Badge tone="muted">Du</Badge> : null}
                </div>
                <div className="truncate font-mono text-[11px] text-muted">
                  {user.principal} · {user.email}
                </div>
              </div>
              {user.id !== me?.id ? (
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => void toggleActive(user)}>
                  {user.active ? "Deaktivieren" : "Aktivieren"}
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => startEdit(user)}>
                Bearbeiten
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
